/**
 * Tokenization Module
 *
 * Splits text into words for RSVP display.
 * Deterministic: same input always produces same output.
 */

/**
 * Tokenize text into an array of words.
 *
 * Rules:
 * - Split on whitespace (spaces, tabs, newlines)
 * - Preserve punctuation attached to words
 * - Filter out empty strings
 * - Trim each word
 *
 * @param text - The input text to tokenize
 * @returns Array of words (empty array if input is empty/whitespace-only)
 */
export function tokenize(text: string): string[] {
  if (!text || typeof text !== 'string') {
    return [];
  }

  return text
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
}

/**
 * Check if a word ends with sentence-ending punctuation.
 * Used for punctuation pause calculation.
 *
 * @param word - The word to check
 * @returns true if word ends with . ? or !
 */
export function endsWithSentenceEnd(word: string): boolean {
  if (!word || word.length === 0) return false;
  const lastChar = word[word.length - 1];
  return lastChar === '.' || lastChar === '?' || lastChar === '!';
}

/**
 * Check if a word ends with clause-separating punctuation.
 * Used for punctuation pause calculation.
 *
 * @param word - The word to check
 * @returns true if word ends with , ; or :
 */
export function endsWithClausePunctuation(word: string): boolean {
  if (!word || word.length === 0) return false;
  const lastChar = word[word.length - 1];
  return lastChar === ',' || lastChar === ';' || lastChar === ':';
}

/**
 * Get the length of a word for font sizing calculations.
 * Strips common punctuation from the count.
 *
 * @param word - The word to measure
 * @returns Letter count (excluding trailing punctuation)
 */
export function getWordLetterCount(word: string): number {
  if (!word) return 0;
  // Remove common trailing punctuation for letter count
  const stripped = word.replace(/[.,;:!?'")\]}>]+$/, '');
  return stripped.length;
}
